import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import CompanyChip from '../../components/asset-company/CompanyChip';
import LicenceForm from '../../components/licences/LicenceForm';
import SeatGiver from '../../components/licences/SeatGiver';
import { useConfirm } from '../../components/ConfirmDialog';
import { downloadFile } from '../../lib/assets';
import { relative } from '../../lib/when';
import {
  licencesApi, errorOf, TYPE_LABEL, PERIOD_LABEL, STATUS_LABEL, DOC_LABEL, statusTone, nextWords, seatWords, seatShare,
  costWords, amount, dayWords, shortDay, holderOf
} from '../../lib/licences';
import '../../components/Assets.css';
import '../../components/licences/Licences.css';

/**
 * ONE LICENCE (119): what it is and what it costs, its dates and the
 * reminders said about them, its seats - who and what holds one, with Free,
 * and Give a seat - the paperwork, the notes and its whole history.
 * Administrators only. Cancelling or marking it expired frees every seat it
 * still has (the server does it, and says so in the history); a licence that
 * never had a seat can be deleted, anything else is cancelled instead.
 */
function Facts({ rows }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!shown.length) return <p className="as-quiet">Nothing recorded.</p>;
  return (
    <dl className="as-facts">
      {shown.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
    </dl>
  );
}

const sizeWords = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function LicenceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [problem, setProblem] = useState('');
  const [options, setOptions] = useState(null);
  const [editing, setEditing] = useState(false);
  const [docType, setDocType] = useState('contract');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const [confirm, confirmDialog] = useConfirm();

  const load = useCallback(() => licencesApi.get(id)
    .then((d) => { setData(d); setError(''); })
    .catch((e) => setError(e?.response?.status === 404 ? 'That licence does not exist.' : errorOf(e, 'Could not load the licence.'))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { licencesApi.options().then(setOptions).catch(() => setOptions({ people: [], companies: [] })); }, []);
  const closeForm = useCallback(() => setEditing(false), []);

  const act = async (fn, fallback) => {
    setProblem('');
    try { await fn(); } catch (e) { setProblem(errorOf(e, fallback)); }
  };

  if (error) {
    return (
      <div className="container">
        <p className="lic-back"><Link to="/assets/licences">← Licences</Link></p>
        <div className="empty-state"><p>{error}</p></div>
      </div>
    );
  }
  if (!data) return <div className="container"><p className="as-quiet" aria-busy="true">Loading the licence…</p></div>;

  const l = data.licence;
  const seats = seatWords(l);
  const nx = nextWords(l);
  const active = l.status === 'active';
  const everUsed = data.seats.length > 0 || data.past.length > 0;

  const setStatus = async (status) => {
    const n = data.seats.length;
    const words = {
      cancelled: { title: `Cancel ${l.product}?`, confirmLabel: 'Cancel the licence', danger: true },
      expired: { title: `Mark ${l.product} as expired?`, confirmLabel: 'Mark as expired', danger: true },
      active: { title: `Reactivate ${l.product}?`, confirmLabel: 'Reactivate' }
    }[status];
    const reason = await confirm({
      ...words,
      message: status === 'active' ? 'It becomes active again with no seats; give them out again from here.'
        : `${n ? `Its ${n} seat${n === 1 ? ' is' : 's are'} freed. ` : ''}No more renewal reminders are sent for it.`,
      prompt: status === 'active' ? null : { label: 'Why (optional)', placeholder: 'Moving to another product', required: false }
    });
    if (reason === false) return;
    await act(async () => { setData(await licencesApi.status(l.id, status, typeof reason === 'string' ? reason : '')); }, 'The status could not be changed.');
  };

  const remove = async () => {
    if (!(await confirm({ title: `Delete ${l.product}?`, message: 'It never had a seat, so nothing is lost. The history keeps a line saying it was deleted.', confirmLabel: 'Delete', danger: true }))) return;
    await act(async () => { await licencesApi.remove(l.id); navigate('/assets/licences'); }, 'The licence could not be deleted.');
  };

  const free = async (s) => {
    if (!(await confirm({ title: `Free ${holderOf(s)}’s seat?`, message: 'The seat goes back to the licence; the history keeps who had it.', confirmLabel: 'Free the seat' }))) return;
    await act(async () => { await licencesApi.unassign(l.id, s.id); await load(); }, 'The seat could not be freed.');
  };

  const upload = async (e) => {
    e.preventDefault();
    const file = fileInput.current?.files?.[0];
    if (!file) { setProblem('Choose a file first.'); return; }
    if (file.size > 10 * 1024 * 1024) { setProblem('That file is larger than 10 MB.'); return; }
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    form.append('doc_type', docType);
    await act(async () => { await licencesApi.upload(l.id, form); if (fileInput.current) fileInput.current.value = ''; await load(); }, 'The file could not be attached.');
    setUploading(false);
  };

  const removeDoc = async (d) => {
    if (!(await confirm({ title: `Remove ${d.file_name}?`, confirmLabel: 'Remove', danger: true }))) return;
    await act(async () => { await licencesApi.removeDocument(l.id, d.id); await load(); }, 'The file could not be removed.');
  };

  return (
    <div className="container" data-testid="licence-page" data-licence={l.id}>
      <p className="lic-back"><Link to="/assets/licences">← Licences</Link></p>
      <div className="lic-head">
        <div className="lic-head-text">
          <h1 className="page-title">{l.product}</h1>
          <p className="page-description" style={{ margin: 0 }}>{[l.vendor, TYPE_LABEL[l.licence_type]].filter(Boolean).join(' · ')}</p>
          <div className="lic-head-chips">
            <span className="as-chip" data-tone={statusTone(l.status)} data-testid="licence-status">{STATUS_LABEL[l.status]}</span>
            {nx && <span className="as-chip" data-tone={nx.tone}>{nx.text}</span>}
            {l.standard && <span className="as-chip" data-tone="info">Every joiner gets it</span>}
            {l.auto_renew && <span className="as-chip">Renews by itself</span>}
          </div>
        </div>
        <div className="lic-head-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setEditing(true)}>Change</button>
          {active ? (
            <button type="button" className="btn btn-plain" onClick={() => setStatus('cancelled')}>Cancel the licence</button>
          ) : (
            <button type="button" className="btn btn-plain" onClick={() => setStatus('active')}>Reactivate</button>
          )}
          {active && l.end_date && <button type="button" className="btn btn-plain" onClick={() => setStatus('expired')}>Mark as expired</button>}
          {!everUsed && <button type="button" className="btn btn-danger" onClick={remove}>Delete</button>}
        </div>
      </div>

      {problem && <p className="as-error" role="alert">{problem}</p>}
      {l.over_allocated && (
        <p className="as-note" data-tone="warning" role="status" data-testid="over-allocated">
          {`Over-allocated: ${l.seats_used} seats are given out and ${l.seats} were bought. Buy ${l.over_by} more, or free ${l.over_by === 1 ? 'one' : l.over_by}.`}
        </p>
      )}
      {l.overdue && (
        <p className="as-note" data-tone="warning" role="status">
          {l.next_kind === 'end' ? 'Its end date has passed.' : 'Its renewal date has passed and nobody has moved it on. If it was renewed, change the date; if it was not, cancel it.'}
        </p>
      )}

      <div className="as-grid">
        <section className="as-panel" data-card="facts">
          <h2>The licence</h2>
          <Facts rows={[
            ['Type', TYPE_LABEL[l.licence_type]],
            ['Seats bought', l.seats === null ? 'No limit' : String(l.seats)],
            ['Reference', l.reference],
            ['Looked after by', l.owner_name ? (
              <Link to={`/assets/licences/people/${l.owner_id}`}>{l.owner_name}{l.owner_active === false ? ' (deactivated)' : ''}</Link>
            ) : 'Nobody in particular'],
            ['For', l.company_name ? <CompanyChip name={l.company_name} color={l.company_color} code={l.company_code} /> : 'The firm']
          ]} />
        </section>
        <section className="as-panel" data-card="cost">
          <h2>Cost</h2>
          <Facts rows={[
            ['Cost', costWords(l)],
            ['Billed', l.billing_period ? PERIOD_LABEL[l.billing_period] : null],
            ['A year', l.annual_cost !== null && l.billing_period !== 'one_off' ? amount(l.annual_cost, l.currency) : null],
            ['Per seat a year', l.annual_cost > 0 && l.seats > 0 ? amount(l.annual_cost / l.seats, l.currency) : null]
          ]} />
        </section>
        <section className="as-panel" data-card="dates">
          <h2>Dates</h2>
          <Facts rows={[
            ['Started', l.start_date ? dayWords(l.start_date) : null],
            ['Renews', l.renewal_date ? dayWords(l.renewal_date) : null],
            ['Ends', l.end_date ? dayWords(l.end_date) : null],
            ['Renews by itself', l.auto_renew ? 'Yes' : 'No'],
            ['Reminders said', data.notices.length
              ? data.notices.map((n) => `${n.stage} days before ${n.kind === 'end' ? 'the end' : 'renewal'} (${shortDay(n.due_on)})`).join('; ')
              : (active ? 'None yet - 90, 30 and 7 days before' : null)]
          ]} />
        </section>
      </div>

      <section className="as-panel lic-wide" data-card="seats" aria-label="Seats">
        <div className="lic-panel-head">
          <h2>Seats</h2>
          <span className="as-chip" data-tone={seats.tone} data-testid="seat-words">{seats.text}</span>
        </div>
        {l.seats !== null && l.seats > 0 && (
          <span className="lic-bar" data-tone={seats.tone === 'success' ? undefined : seats.tone} aria-hidden="true" style={{ marginBottom: '0.75rem' }}>
            <span style={{ width: `${seatShare(l)}%` }} />
          </span>
        )}
        <SeatGiver licence={l} people={options?.people || []}
          heldBy={{ users: data.seats.filter((s) => s.user_id).map((s) => s.user_id), assets: data.seats.filter((s) => s.asset_id).map((s) => s.asset_id) }}
          onGiven={load} />
        {data.seats.length === 0 ? (
          <p className="as-quiet" data-testid="no-seats">{active ? 'No seats given out yet.' : 'No seats.'}</p>
        ) : (
          <ul className="lic-seats" aria-label="Who has a seat">
            {data.seats.map((s) => (
              <li key={s.id} data-seat={s.id}>
                <span className="lic-seat-who">
                  <span className="lic-seat-kind">{s.user_id ? 'Person' : 'Device'}</span>
                  {s.user_id
                    ? <Link to={`/assets/licences/people/${s.user_id}`}>{s.user_name}</Link>
                    : <Link to={`/assets/${s.asset_id}`}>{holderOf(s)}</Link>}
                  {s.user_id && s.user_active === false && <span className="as-chip" data-tone="warning" style={{ marginLeft: '0.4rem' }}>Deactivated</span>}
                  <span className="lic-seat-sub">
                    {[s.user_id ? (s.user_department || s.user_email) : (s.asset_holder_name ? `with ${s.asset_holder_name}` : null),
                      `given ${relative(s.assigned_at)}${s.assigned_by_name ? ` by ${s.assigned_by_name}` : ''}`, s.note].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <button type="button" className="btn btn-secondary btn-small" onClick={() => free(s)}
                  aria-label={`Free the seat held by ${holderOf(s)}`}>Free</button>
              </li>
            ))}
          </ul>
        )}
        {data.past.length > 0 && (
          <details style={{ marginTop: '0.75rem' }}>
            <summary className="as-quiet">{`Held before (${data.past.length})`}</summary>
            <ul className="lic-seats" aria-label="Seats held before">
              {data.past.map((s) => (
                <li key={s.id}>
                  <span className="lic-seat-who">
                    {holderOf(s)}
                    <span className="lic-seat-sub">
                      {`${shortDay(s.assigned_at)} to ${shortDay(s.freed_at)}${s.freed_reason ? ` · ${s.freed_reason}` : ''}${s.freed_by_name ? ` · freed by ${s.freed_by_name}` : ''}`}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <div className="as-grid">
        <section className="as-panel" data-card="documents" aria-label="Documents">
          <h2>Documents</h2>
          {data.documents.length === 0 ? <p className="as-quiet">No contract or invoice attached.</p> : (
            <ul className="lic-docs">
              {data.documents.map((d) => (
                <li key={d.id}>
                  <span className="lic-doc-name">
                    <button type="button" className="btn btn-plain btn-small" style={{ paddingInline: 0 }}
                      onClick={() => act(() => downloadFile(licencesApi.documentPath(l.id, d.id), d.file_name), 'The file could not be downloaded.')}>
                      {d.file_name}
                    </button>
                    <span className="lic-seat-sub">{`${DOC_LABEL[d.doc_type] || d.doc_type} · ${sizeWords(d.byte_size)} · ${shortDay(d.created_at)}${d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ''}`}</span>
                  </span>
                  <button type="button" className="btn btn-plain btn-small" onClick={() => removeDoc(d)} aria-label={`Remove ${d.file_name}`}>Remove</button>
                </li>
              ))}
            </ul>
          )}
          <form className="lic-upload" onSubmit={upload}>
            <label>
              Kind
              <select value={docType} onChange={(e) => setDocType(e.target.value)} aria-label="Kind of document">
                {Object.entries(DOC_LABEL).map(([v, w]) => <option key={v} value={v}>{w}</option>)}
              </select>
            </label>
            <label>
              File (10 MB at most)
              <input type="file" ref={fileInput} aria-label="File to attach" />
            </label>
            <button type="submit" className="btn btn-secondary" disabled={uploading}>{uploading ? 'Attaching…' : 'Attach'}</button>
          </form>
        </section>
        <section className="as-panel" data-card="notes">
          <h2>Notes</h2>
          {l.notes ? <p className="lic-notes">{l.notes}</p> : <p className="as-quiet">No notes.</p>}
        </section>
      </div>

      <section className="as-panel lic-wide" data-card="history" aria-label="History">
        <h2>History</h2>
        {data.history.length === 0 ? <p className="as-quiet">Nothing yet.</p> : (
          <ul className="as-history">
            {data.history.map((h) => (
              <li key={h.id}>
                <div>{h.said}</div>
                <div className="as-quiet" style={{ fontSize: '0.8rem' }}>
                  {`${h.actor_name}${h.on_behalf_by ? ` (by ${h.on_behalf_by})` : ''} · ${relative(h.at)}`}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <LicenceForm licence={l} options={options} onClose={closeForm}
          onSaved={(saved) => { setEditing(false); setData(saved); }} />
      )}
      {confirmDialog}
    </div>
  );
}
